import { atom, useAtom } from "jotai";
import { toast } from "react-toastify";
import React from "react";
import {
  onSearchLeadsAuto,
  onDeleteLeadAuto,
} from "../../../services/graal-api/leadAutoService";
import useManagers from "../../../hooks/useManagers/useManagers";
import useGlobalError from "../../../hooks/useGlobalError/useGlobalError";
import useStore from "../../../context/useStore";
import { formatLeadAutoData } from "./utils/formatLeadAutoData";
import { exporterLeadsAutoJson } from "./utils/exporterLeadsAutoJson";
import { withRetry } from "../../../helpers/withRetry";
import { LEAD_AUTO_FILTERS_DEFAULT } from "./leadAutoFiltersDefault";

export const leadAutoListAtom = atom(null);
export const leadAutoFiltersAtom = atom({ ...LEAD_AUTO_FILTERS_DEFAULT });
export const isLoadingAtom = atom(false);
export const nbLeadAtom = atom(0);
export const hasFilterChangedAtom = atom(false);
export const isExportingAllAtom = atom(false);
export const exportProgressAtom = atom({ current: 0, total: 0 });

const EXPORT_BATCH_SIZE = 3;

export const LeadAutoListContext = React.createContext();
export const LeadAutoListProvider = ({ value, children }) => {
  return (
    <LeadAutoListContext.Provider value={value}>
      {children}
    </LeadAutoListContext.Provider>
  );
};

const useLeadAutoList = () => {
  const [state] = useStore();
  const { LEAD_TASKS } = state.constants.items;
  const [leadAutoList, setLeadAutoList] = useAtom(leadAutoListAtom);
  const [leadAutoFilters, setLeadAutoFilters] = useAtom(leadAutoFiltersAtom);
  const [nbLead, setNbLead] = useAtom(nbLeadAtom);
  const { managers } = useManagers();
  const [isLoading, setIsLoading] = useAtom(isLoadingAtom);
  const { globalErrorActions } = useGlobalError();
  const [hasFilterChanged, setHasFilterChanged] = useAtom(hasFilterChangedAtom);
  const [isExportingAll, setIsExportingAll] = useAtom(isExportingAllAtom);
  const [exportProgress, setExportProgress] = useAtom(exportProgressAtom);

  const actions = {
    onFetchLeadAutoList: async (filters, overrideFilters = null) => {
      if (overrideFilters) {
        setLeadAutoFilters(overrideFilters);
      }

      if (hasFilterChanged) {
        filters = {
          ...filters,
          page: 1,
        };
        setLeadAutoFilters(filters);
      }

      setIsLoading(true);
      onSearchLeadsAuto(filters)
        .then((results) => {
          setLeadAutoList(actions.getFormattedLeads(results.data.data));
          setNbLead(results.data.meta.total);
          setIsLoading(false);
        })
        .catch((err) => {
          console.log("err", err);
          globalErrorActions.handleGlobalError(err.response?.status, {
            callback: () => {
              actions.onFetchLeadAutoList(filters);
            },
          });
          setIsLoading(false);
        });
      setHasFilterChanged(false);
    },
    // Prend un objet de filtres partiel (jamais un event DOM) : seul
    // useLeadAutoFilters.js appelle cette fonction, sous forme de patch.
    onFiltersChange: (e) => {
      setLeadAutoFilters((old) => ({ ...old, ...e }));
      setHasFilterChanged(true);
    },
    updatePage: (page) => {
      const newFilters = { ...leadAutoFilters, page };
      setLeadAutoFilters(newFilters);
      actions.onFetchLeadAutoList(newFilters);
    },
    getFormattedLeads: (apiLeads) => {
      return formatLeadAutoData(apiLeads, managers, LEAD_TASKS);
    },
    onDeleteLead: (leadId) => {
      const oldList = [...leadAutoList];
      setLeadAutoList((old) => old.filter((l) => l.id !== leadId));

      onDeleteLeadAuto(leadId)
        .then(() => toast.success("Ce lead a bien été supprimé"))
        .catch((err) => {
          toast.error(
            `Erreur lors de la suppression du lead ${err.response?.data?.message}`,
          );

          globalErrorActions.handleGlobalError(err.response?.status, {
            callback: () => actions.onDeleteLead(leadId),
          });
          setLeadAutoList(oldList);
        });
    },
    updateLeadInList: (leadId, updatedData) => {
      setLeadAutoList((currentList) =>
        currentList.map((lead) =>
          lead.id === leadId ? { ...lead, ...updatedData } : lead,
        ),
      );
    },
    handleExportAllJSON: async () => {
      if (isExportingAll) return;

      setIsExportingAll(true);
      try {
        const firstResponse = await withRetry(() =>
          onSearchLeadsAuto({
            ...leadAutoFilters,
            page: 1,
            avecJournalAgent: true,
          }),
        );
        const total = firstResponse.data.meta.total;
        const detectedPageSize = firstResponse.data.data?.length || 0;
        if (!detectedPageSize) {
          toast.error("Page 1 vide, export annulé");
          return;
        }
        const totalPages = Math.max(1, Math.ceil(total / detectedPageSize));

        setExportProgress({ current: 1, total: totalPages });

        const allLeads = [...firstResponse.data.data];

        for (
          let batchStart = 2;
          batchStart <= totalPages;
          batchStart += EXPORT_BATCH_SIZE
        ) {
          const batchPages = [];
          for (
            let p = batchStart;
            p < batchStart + EXPORT_BATCH_SIZE && p <= totalPages;
            p++
          ) {
            batchPages.push(p);
          }

          const batchResults = await Promise.all(
            batchPages.map((page) =>
              withRetry(() =>
                onSearchLeadsAuto({
                  ...leadAutoFilters,
                  page,
                  avecJournalAgent: true,
                }),
              ),
            ),
          );

          batchResults.forEach((res) => {
            allLeads.push(...res.data.data);
          });

          setExportProgress({
            current: Math.min(
              batchStart + EXPORT_BATCH_SIZE - 1,
              totalPages,
            ),
            total: totalPages,
          });
        }

        const exportData = exporterLeadsAutoJson(allLeads, LEAD_TASKS);

        const jsonString = JSON.stringify(exportData, null, 2);
        const blob = new Blob([jsonString], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `leads_auto_${
          new Date().toISOString().split("T")[0]
        }.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        toast.success(`${exportData.length} leads exportés`);
      } catch (err) {
        console.log("err", err);
        toast.error("Erreur lors de l'export des leads");
        globalErrorActions.handleGlobalError(err.response?.status);
      } finally {
        setIsExportingAll(false);
        setExportProgress({ current: 0, total: 0 });
      }
    },
    resetLeadAutoFilters: () => {
      setLeadAutoFilters({ ...LEAD_AUTO_FILTERS_DEFAULT });
    },
  };

  return {
    leadAutoList,
    leadAutoFilters,
    actions,
    isLoading,
    nbLead,
    isExportingAll,
    exportProgress,
  };
};

export default useLeadAutoList;
